/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Flex, Box, IconButton, Button, Dialog, Text, Popover, TextArea, Heading } from '@radix-ui/themes';
import { RefreshCw, Repeat, Menu } from 'lucide-react';
import {
  ExtJS,
  handleExtJsUnsavedChanges,
  restClient,
  useTheme,
  SearchRadix,
  ThemeSwitcher,
  Tooltip,
  getHeritageEquivalent,
  heritageToPreviewPath,
  useSideNavbarOpenState,
} from '@sonatype/nexus-ui-plugin';
import { useRouter } from '@uirouter/react';

import proLogo from '../../../../art/logos/logo-pro-edition-header.svg';
import proDarkLogo from '../../../../art/logos/logo-pro-edition-header-dark.svg';

import './Globalheader.scss';
import HelpMenu from './HelpMenu';
import LoginAndUserButton from './LogInAndUserProfileMenu';
import SystemStatus from './SystemStatus';
import { refreshReactPage } from '../../routerConfig/routerUtils';
import { ROUTE_NAMES } from '../../routerConfig/routeNames/routeNames';

const COMMUNITY = 'COMMUNITY';
const PRO = 'PRO';

export default function GlobalHeader() {
  const router = useRouter();
  const { effectiveTheme, setTheme } = useTheme();

  // Edition detection - use state().getEdition() which is the established cloud pattern.
  // ExtJS.useStatus() may not return correct edition in cloud environments.
  const edition = ExtJS.useState(() => ExtJS.state().getEdition());

  // Use useUser for authentication state - cloud pattern: !!user means logged in
  const user = ExtJS.useUser();
  const isLoggedIn = !!user;
  const isAnonymous = !isLoggedIn;

  // Read context path and version synchronously — static/cosmetic values safe to read any time.
  let contextPath = '';
  let version = 'Unknown';
  try {
    const state = ExtJS.state();
    if (state) {
      contextPath = state.getValue('nexus-context-path', '') || '';
      version = state.getVersionMajorMinor?.() ?? state.getVersion?.() ?? 'Unknown';
    }
  } catch (e) {
    // State not available during initialization - defaults are fine
  }

  // Visibility flags — must be reactive so the button appears when ExtJS state loads asynchronously.
  const [canAccessPreviewUi, setCanAccessPreviewUi] = useState(false);
  const [isDisableLegacyUi, setDisableLegacyUi] = useState(false);
  const [isDisableSwitchFeedback, setDisableSwitchFeedback] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    try {
      const state = ExtJS.state();
      if (!state) return;

      setDisableLegacyUi(state.getValue('disableLegacyUi', false) ?? false);
      setDisableSwitchFeedback(state.getValue('disableSwitchFeedback', false) ?? false);

      if (isLoggedIn) {
        // Only upgrade false → true; never revoke while logged in (avoids flash on re-render).
        if (!canAccessPreviewUi) {
          const loggedInEnabled = state.getValue('loggedInEnabled', false) ?? false;
          if (loggedInEnabled) setCanAccessPreviewUi(true);
        }
      } else {
        // Anonymous: always re-evaluate (no caching needed — anonymous state is stable).
        const anonymousEnabled = state.getValue('anonymousEnabled', false) ?? false;
        setCanAccessPreviewUi(anonymousEnabled);
      }
    } catch {
      if (!isLoggedIn) setCanAccessPreviewUi(false);
    }
    setSettingsLoaded(true);
  }, [isLoggedIn, user, canAccessPreviewUi]);

  const [isSidebarOpen] = useSideNavbarOpenState(true);
  const onSidebarToggle = useCallback(() => {
    window.dispatchEvent(new CustomEvent('nx-sidebar-toggle'));
  }, []);

  const [heritageUnavailableOpen, setHeritageUnavailableOpen] = useState(false);
  const [uiSwitchPopoverOpen, setUiSwitchPopoverOpen] = useState(false);
  const [classicUiFeedback, setClassicUiFeedback] = useState('');
  const useClassicUiButtonRef = useRef(null);

  // Track hash changes for Preview UI toggle state
  const [currentPath, setCurrentPath] = useState(() => window.location.hash.substring(1));

  useEffect(() => {
    const handleLocationChange = () => {
      const hash = window.location.hash.substring(1);
      setCurrentPath(hash || '');
    };

    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const isPreviewUI = currentPath.startsWith('preview');

  // Auto-redirect out of Preview UI if user loses access (guard: wait for settings to load first)
  useEffect(() => {
    if (!settingsLoaded) return;
    if (isPreviewUI && !canAccessPreviewUi) {
      setTheme('light');
      const defaultPath = currentPath.replace(/^preview\//, '') || 'browse/welcome';
      window.location.hash = defaultPath;
    }
  }, [settingsLoaded, isPreviewUI, canAccessPreviewUi, currentPath, setTheme]);

  const switchToClassicUI = useCallback(() => {
    const heritagePath = getHeritageEquivalent(currentPath);
    if (heritagePath === null) {
      setHeritageUnavailableOpen(true);
      setUiSwitchPopoverOpen(false);
      return;
    }

    if (!isDisableSwitchFeedback) {
      restClient.post('/service/rest/internal/ui/switch-feedback', {
        edition: edition ?? 'Unknown',
        version,
        feedback: classicUiFeedback.trim().slice(0, 500),
      }).catch(() => {});
    }

    sessionStorage.setItem('user_requested_legacy', 'true');
    setTheme('light');
    setUiSwitchPopoverOpen(false);
    setClassicUiFeedback('');
    window.location.hash = heritagePath;
  }, [classicUiFeedback, currentPath, edition, isDisableSwitchFeedback, setTheme, version]);

  const switchToNexusOneUI = useCallback(() => {
    const newPath = heritageToPreviewPath(currentPath);
    window.location.hash = `#${newPath}`;
    sessionStorage.removeItem('user_requested_legacy');
  }, [currentPath]);

  const handleUiSwitchPopoverOpenChange = useCallback((open) => {
    setUiSwitchPopoverOpen(open);
    if (!open) {
      setClassicUiFeedback('');
    }
  }, []);

  const handlePopoverOpenAutoFocus = useCallback((e) => {
    e.preventDefault();
    requestAnimationFrame(() => {
      useClassicUiButtonRef.current?.focus();
    });
  }, []);

  const onRefreshClick = () => {
    const menuCtrl =
      window.Ext && Ext.getApplication && Ext.getApplication().getController
        ? Ext.getApplication().getController('Menu')
        : null;

    handleExtJsUnsavedChanges(menuCtrl, () => {
      if (ExtJS.isExtJsRendered()) {
        ExtJS.refresh();
      } else {
        refreshReactPage(router);
      }
    });
  };

  function getLogo() {
    const isDark = effectiveTheme === 'dark';
    return isDark ? proDarkLogo : proLogo;
  }

  function getEditionText() {
    return edition === COMMUNITY ? 'Community'
        : edition === PRO ? 'Professional'
        : 'Core';
  }

  return (
    <div className="nxrm-global-header nxrm-global-header-radix nx-global-header-2" role="banner">
      <Flex
        align="center"
        justify="between"
        style={{
          height: '60px',
          width: '100%',
          padding: '0 16px',
          boxSizing: 'border-box',
          backgroundColor: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)'
        }}
      >
        {/* Left cluster: Sidebar toggle + Logo */}
        <Flex align="center" gap="3" style={{ flexShrink: 0 }}>
          <Tooltip content={isSidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}>
            <IconButton
              variant="outline"
              size="2"
              color="gray"
              aria-label={isSidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
              onClick={onSidebarToggle}
              data-testid="sidebar-toggle-button"
            >
              <Menu size={16} />
            </IconButton>
          </Tooltip>
          <Box asChild style={{ flexShrink: 0 }}>
            <a href={`${contextPath || '/'}#${isPreviewUI ? 'preview/browse/welcome' : 'browse/welcome'}`} title="Home" aria-label="Home">
              <img
                src={getLogo()}
                alt={`Sonatype Nexus Repository ${getEditionText()}`}
                style={{ height: '32px', display: 'block' }}
              />
            </a>
          </Box>
        </Flex>

        {/* Center: Search Bar - same autocomplete experience as self-hosted */}
        <Box style={{ flex: 1, maxWidth: '500px', margin: '0 24px' }}>
          <SearchRadix
            isPreviewUI={isPreviewUI}
            routes={{
              searchGeneric: ROUTE_NAMES.BROWSE.SEARCH.GENERIC,
              previewSearchUnified: 'preview.browse.search.unified',
              previewSearchComponent: 'preview.browse.search.component',
            }}
          />
        </Box>

        {/* Right side controls */}
        <Flex align="center" gap="3" style={{ flexShrink: 0 }}>
          {canAccessPreviewUi && !isDisableLegacyUi && (
            isPreviewUI ? (
              isDisableSwitchFeedback ? (
                <Tooltip content="Switch to Classic UI">
                  <Button
                    variant="soft"
                    color="green"
                    onClick={switchToClassicUI}
                    style={{ cursor: 'pointer' }}
                    aria-label="Switch to Classic UI"
                    title="Switch to Classic UI"
                    data-testid="ui-toggle-button"
                  >
                    <Flex align="center" gap="2">
                      <Repeat size={16} aria-hidden />
                      Switch to Classic UI
                    </Flex>
                  </Button>
                </Tooltip>
              ) : (
                <Popover.Root open={uiSwitchPopoverOpen} onOpenChange={handleUiSwitchPopoverOpenChange}>
                  <Tooltip content={uiSwitchPopoverOpen ? undefined : 'Switch to Classic UI'}>
                    <Popover.Trigger asChild>
                      <Button
                        variant="soft"
                        color="green"
                        style={{ cursor: 'pointer' }}
                        aria-label="Switch to Classic UI"
                        aria-haspopup="dialog"
                        title="Switch to Classic UI"
                        data-testid="ui-toggle-button"
                      >
                        <Flex align="center" gap="2">
                          <Repeat size={16} aria-hidden />
                          Switch to Classic UI
                        </Flex>
                      </Button>
                    </Popover.Trigger>
                  </Tooltip>
                  <Popover.Content
                    width="360px"
                    aria-labelledby="ui-switch-popover-title"
                    aria-describedby="ui-switch-popover-desc"
                    onOpenAutoFocus={handlePopoverOpenAutoFocus}
                  >
                    <Flex direction="column" gap="4">
                      <Box>
                        <Heading id="ui-switch-popover-title" size="3" mb="2">
                          Trying the new Nexus One UI
                        </Heading>
                        <Text id="ui-switch-popover-desc" size="2" color="gray" as="p">
                          Prefer the classic experience? Let us know why you&apos;re switching back.
                        </Text>
                      </Box>
                      <Flex direction="column" gap="3">
                        <TextArea
                          id="ui-feedback-textarea"
                          placeholder="Share your feedback..."
                          value={classicUiFeedback}
                          onChange={(e) => setClassicUiFeedback(e.target.value)}
                          rows={3}
                          aria-label="Optional feedback"
                        />
                        <Button
                          ref={useClassicUiButtonRef}
                          variant="soft"
                          color="green"
                          size="2"
                          style={{ cursor: 'pointer' }}
                          onClick={switchToClassicUI}
                          aria-label="Use Classic UI"
                          title="Use Classic UI"
                        >
                          Use Classic UI
                        </Button>
                      </Flex>
                    </Flex>
                  </Popover.Content>
                </Popover.Root>
              )
            ) : (
              <Tooltip content="Switch to Nexus One UI">
                <Button
                  variant="soft"
                  color="green"
                  onClick={switchToNexusOneUI}
                  style={{ cursor: 'pointer' }}
                  aria-label="Switch to Nexus One UI"
                  title="Switch to Nexus One UI"
                  data-testid="ui-toggle-button"
                >
                  <Flex align="center" gap="2">
                    <Repeat size={16} aria-hidden />
                    Switch to Nexus One UI
                  </Flex>
                </Button>
              </Tooltip>
            )
          )}

          {/* Theme Switcher - only available in Preview UI when user has access */}
          {isPreviewUI && canAccessPreviewUi ? (
            <ThemeSwitcher />
          ) : null}

          {/* System Status */}
          <SystemStatus />

          {/* Refresh Button */}
          <IconButton
            variant="ghost"
            title="Refresh"
            aria-label="Refresh"
            onClick={onRefreshClick}
          >
            <RefreshCw size={18} />
          </IconButton>

          {/* Help Menu */}
          <HelpMenu />

          {/* User Profile / Login */}
          <LoginAndUserButton />
        </Flex>
      </Flex>

      <Dialog.Root open={heritageUnavailableOpen} onOpenChange={(open) => !open && setHeritageUnavailableOpen(false)}>
        <Dialog.Content maxWidth="450px" aria-describedby={undefined}>
          <Dialog.Title>Not Available in Classic UI</Dialog.Title>
          <Text size="2" color="gray" as="p" mt="2">
            This view is only available in the Nexus One UI. The Classic UI doesn&apos;t have an equivalent page for this
            feature.
          </Text>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" size="2">
                Stay on Nexus One UI
              </Button>
            </Dialog.Close>
            <Button
              variant="solid"
              color="blue"
              size="2"
              onClick={() => {
                setHeritageUnavailableOpen(false);
                sessionStorage.setItem('user_requested_legacy', 'true');
                setTheme('light');
                window.location.hash = 'browse/welcome';
              }}
            >
              Go to Classic Dashboard
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
